import type { ReactNode } from 'react'
import type {
  Ingredient,
  IngredientMarkedType,
  Mix,
  Recipe,
  WebSocketEvent,
  UpdateMixRequest,
  Ownership,
  CreateIngredientRequest,
  CreateMixRequest,
  DeleteMixRequest,
} from '@/types'
import type { InvitationDTO } from '@/types'

import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'

import { useAuth } from './AuthContext'
import { useSupabaseRealtime } from '@/hooks'
import { convertRealtimeRowToMix } from '@/hooks/useSupabaseRealtime'
import { supabase } from '@/lib/supabase'
import { ingredientService, invitationService, mixService } from '@/services'
import { toast } from 'sonner'
import { CheckCircle } from 'lucide-react'

type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'reconnecting' | 'error'

export interface WebSocketContextValue {
  clearRecipes: (mixId: string) => void
  connectionState: ConnectionState
  createIngredient: (mixId: string, request: CreateIngredientRequest) => void
  createInvitation: (mixId: string) => void
  createMix: (request: CreateMixRequest) => void
  deleteIngredient: (mixId: string, ingredientId: string, mealSlotId?: string | null, scheduledAt?: string | null) => void
  deleteMix: (mixId: string) => void
  deleteRecipe: (mixId: string, recipeId: string) => void
  getIngredients: (mixId: string) => Ingredient[]
  getOwnership: (mixId: string) => Ownership
  getRecipe: (mixId: string, recipeId: string) => Recipe | undefined
  getRecipes: (mix: string) => Recipe[]
  getSelectedRecipes: (mixId: string) => Recipe[]
  getSelectedRecipesIngredients: (mixId: string) => Ingredient[]
  isMix: (mixId: string) => boolean
  loading: boolean
  markIngredient: (mixId: string, ingredientId: string, markedType: IngredientMarkedType, mealSlotId?: string | null, scheduledAt?: string | null) => void
  mixes: Mix[]
  onEvent: (callback: (event: WebSocketEvent) => void) => () => void
  selectRecipe: (mixId: string, recipeId: string, selected: boolean) => void
  favoriteRecipe: (mixId: string, recipeId: string, action: 'add' | 'remove') => void
  setLoading: (loading: boolean) => void
  invitationsMap: Record<string, InvitationDTO>
}

export interface WebSocketProviderProps {
  children: ReactNode
}

const WebSocketContext = createContext<WebSocketContextValue | null>(null)

export function WebSocketProvider({ children }: WebSocketProviderProps) {
  const [mixesMap, setMixesMap] = useState<Record<string, Mix>>({})
  const [mixes, setMixes] = useState<Mix[]>([])
  const [invitationsMap, setInvitationsMap] = useState<Record<string, InvitationDTO>>({})
  const [loading, setLoading] = useState(true)
  const hasInitializedRef = useRef(false)
  const { user } = useAuth()

  // Phase 1.7: Internal event bus replaces WebSocket for RecipeDialog
  const eventCallbacksRef = useRef<Set<(event: WebSocketEvent) => void>>(new Set())

  const onEvent = useCallback((callback: (event: WebSocketEvent) => void) => {
    eventCallbacksRef.current.add(callback)
    return () => { eventCallbacksRef.current.delete(callback) }
  }, [])

  // WebSocket is gone — Realtime is always connected
  const connectionState = 'connected' as const

  // ─── Phase 1.4: Realtime callbacks drive state ───────────────────────────

  const handleMixUpdateFromRealtime = useCallback(async (mix: Mix) => {
    // When a new contributor accepts an invitation, Realtime sends an UPDATE
    // event (not INSERT) because the mix row already existed. The payload is
    // partial — only changed columns (contributors, updated_at) are included.
    // convertRealtimeRowToMix defaults missing JSONB columns to []
    // so the mix appears empty. For a newly-discovered mix we must NOT trust
    // these empty arrays; fetch the full row instead.
    //
    // Additionally, Supabase Realtime may drop large JSONB columns (e.g.
    // ingredients) while keeping smaller ones (e.g. recipes). Detect this by
    // checking if one array is empty while the other has data and the existing
    // state had data in the supposedly-empty array.
    let needsFetch = false

    setMixesMap(prev => {
      const existing = prev[mix.id]
      const isEmptyPayload = mix.recipes.length === 0 && mix.ingredients.length === 0
      const shouldPreserveRecipes =
        mix.recipes.length === 0 &&
        existing != null &&
        existing.recipes.length > 0
      const shouldPreserveIngredients =
        mix.ingredients.length === 0 &&
        existing != null &&
        existing.ingredients.length > 0
      const isPartialPayload = isEmptyPayload || shouldPreserveRecipes || shouldPreserveIngredients

      if (!existing && isPartialPayload) {
        needsFetch = true
        return prev
      }

      const mergedMix = { ...mix }
      if (shouldPreserveRecipes) mergedMix.recipes = existing!.recipes
      if (shouldPreserveIngredients) mergedMix.ingredients = existing!.ingredients
      return { ...prev, [mix.id]: mergedMix }
    })

    setMixes(prev => {
      const map = { ...Object.fromEntries(prev.map(m => [m.id, m])) }
      const existing = map[mix.id]
      const isEmptyPayload = mix.recipes.length === 0 && mix.ingredients.length === 0
      const shouldPreserveRecipes =
        mix.recipes.length === 0 &&
        existing != null &&
        existing.recipes.length > 0
      const shouldPreserveIngredients =
        mix.ingredients.length === 0 &&
        existing != null &&
        existing.ingredients.length > 0
      const isPartialPayload = isEmptyPayload || shouldPreserveRecipes || shouldPreserveIngredients

      if (!existing && isPartialPayload) {
        return Object.values(map).filter(m => !m.archivedAt)
      }

      const mergedMix = { ...mix }
      if (shouldPreserveRecipes) mergedMix.recipes = existing!.recipes
      if (shouldPreserveIngredients) mergedMix.ingredients = existing!.ingredients
      map[mix.id] = mergedMix
      return Object.values(map).filter(m => !m.archivedAt)
    })

    if (needsFetch) {
      // Fetch the full mix row for this newly-discovered partial payload
      const { data, error } = await supabase
        .from('mixes')
        .select('*')
        .eq('id', mix.id)
        .single()
      if (!error && data) {
        const fullMix = convertRealtimeRowToMix(data as Record<string, unknown>, user?.id)
        if (fullMix) {
          setMixesMap(prev => ({ ...prev, [fullMix.id]: fullMix }))
          setMixes(prev => {
            const map = { ...Object.fromEntries(prev.map(m => [m.id, m])) }
            map[fullMix.id] = fullMix
            return Object.values(map).filter(m => !m.archivedAt)
          })
          showMixToasts(fullMix)
          eventCallbacksRef.current.forEach(cb => cb({
            type: 'ENTITY_MUTATIONS',
            payload: { mutations: [{ type: 'UPDATE', entity: 'MIX', data: fullMix, timestamp: new Date().toISOString() }] },
            timestamp: new Date().toISOString(),
          }))
          return
        }
      }
    }

    // Phase 1.7: Toast moved from WebSocket ENTITY_MUTATIONS handler
    showMixToasts(mix)
    // Phase 1.7: Emit synthetic ENTITY_MUTATIONS for RecipeDialog
    eventCallbacksRef.current.forEach(cb => cb({
      type: 'ENTITY_MUTATIONS',
      payload: { mutations: [{ type: 'UPDATE', entity: 'MIX', data: mix, timestamp: new Date().toISOString() }] },
      timestamp: new Date().toISOString(),
    }))
  }, [user?.id])

  const handleMixInsertFromRealtime = useCallback((mix: Mix) => {
    setMixesMap(prev => ({ ...prev, [mix.id]: mix }))
    setMixes(prev => {
      if (prev.some(m => m.id === mix.id)) return prev
      return [...prev, mix].filter(m => !m.archivedAt)
    })
    // Phase 1.7: Emit synthetic ENTITY_MUTATIONS for RecipeDialog
    eventCallbacksRef.current.forEach(cb => cb({
      type: 'ENTITY_MUTATIONS',
      payload: { mutations: [{ type: 'CREATE', entity: 'MIX', data: mix, timestamp: new Date().toISOString() }] },
      timestamp: new Date().toISOString(),
    }))
  }, [])

  const handleMixDeleteFromRealtime = useCallback((id: string) => {
    setMixesMap(prev => {
      const next = { ...prev }
      delete next[id]
      return next
    })
    setMixes(prev => prev.filter(m => m.id !== id))
    // Phase 1.7: Emit synthetic ENTITY_MUTATIONS for RecipeDialog
    eventCallbacksRef.current.forEach(cb => cb({
      type: 'ENTITY_MUTATIONS',
      payload: { mutations: [{ type: 'DELETE', entity: 'MIX', data: { id } as Mix, timestamp: new Date().toISOString() }] },
      timestamp: new Date().toISOString(),
    }))
  }, [])

  const handleInvitationUpdateFromRealtime = useCallback((invitation: InvitationDTO) => {
    setInvitationsMap(prev => ({ ...prev, [invitation.id]: invitation }))
    // Phase 1.7: Toast moved from WebSocket ENTITY_MUTATIONS handler
    if (shouldShowAcceptanceNotification(invitation)) {
      showInvitationAcceptanceToast(invitation)
    }
    // Phase 1.7: Emit synthetic ENTITY_MUTATIONS for RecipeDialog
    eventCallbacksRef.current.forEach(cb => cb({
      type: 'ENTITY_MUTATIONS',
      payload: { mutations: [{ type: 'UPDATE', entity: 'INVITATION', data: invitation, timestamp: new Date().toISOString() }] },
      timestamp: new Date().toISOString(),
    }))
  }, [])

  const handleInvitationInsertFromRealtime = useCallback((invitation: InvitationDTO) => {
    setInvitationsMap(prev => ({ ...prev, [invitation.id]: invitation }))
    // Phase 1.7: Emit synthetic ENTITY_MUTATIONS for RecipeDialog
    eventCallbacksRef.current.forEach(cb => cb({
      type: 'ENTITY_MUTATIONS',
      payload: { mutations: [{ type: 'CREATE', entity: 'INVITATION', data: invitation, timestamp: new Date().toISOString() }] },
      timestamp: new Date().toISOString(),
    }))
  }, [])

  const handleInvitationDeleteFromRealtime = useCallback((id: string) => {
    setInvitationsMap(prev => {
      const next = { ...prev }
      delete next[id]
      return next
    })
    // Phase 1.7: Emit synthetic ENTITY_MUTATIONS for RecipeDialog
    eventCallbacksRef.current.forEach(cb => cb({
      type: 'ENTITY_MUTATIONS',
      payload: { mutations: [{ type: 'DELETE', entity: 'INVITATION', data: { id } as InvitationDTO, timestamp: new Date().toISOString() }] },
      timestamp: new Date().toISOString(),
    }))
  }, [])

  const handleRecipeProgressFromRealtime = useCallback((payload: Record<string, unknown>) => {
    eventCallbacksRef.current.forEach(cb => cb({
      type: 'RECIPE_PROGRESS',
      payload: {
        request: {
          id: `recipe-${Date.now()}`,
          sender: { id: (payload.requester_id as string) || '', name: '' },
          url: (payload.url as string) || '',
          sentAt: (payload.created_at as string) || new Date().toISOString(),
        },
        phase: (payload.phase as string) || '',
        status: (payload.status as string) || '',
        message: (payload.message as string) || '',
      },
      timestamp: new Date().toISOString(),
    }))
  }, [])

  // Realtime subscription — drives state via callbacks
  useSupabaseRealtime({
    onMixInsert: handleMixInsertFromRealtime,
    onMixUpdate: handleMixUpdateFromRealtime,
    onMixDelete: handleMixDeleteFromRealtime,
    onInvitationInsert: handleInvitationInsertFromRealtime,
    onInvitationUpdate: handleInvitationUpdateFromRealtime,
    onInvitationDelete: handleInvitationDeleteFromRealtime,
    onRecipeProgress: handleRecipeProgressFromRealtime,
  })

  // Phase 1.1: Load initial state directly from Supabase (replaces USER_INITIALIZE)
  useEffect(() => {
    if (!user?.id) {
      return
    }

    const loadInitialState = async () => {
      if (hasInitializedRef.current) return
      setLoading(true)

      console.debug('[Supabase] Loading initial state for user:', user.id)

      const { data, error } = await supabase
        .from('mixes')
        .select('*')
        .or(`owner_id.eq.${user.id},contributors.cs.{${user.id}}`)
        .is('archived_at', null)

      if (error) {
        console.error('[Supabase] Failed to load initial mixes:', error)
        return
      }

      const mixesRecord: Record<string, Mix> = {}
      for (const row of data || []) {
        const mix = convertRealtimeRowToMix(row as Record<string, unknown>, user.id)
        if (mix) {
          mixesRecord[mix.id] = mix
        }
      }

      console.debug('[Supabase] Loaded mixes:', Object.keys(mixesRecord).length)
      setMixesMap(mixesRecord)
      setMixes(Object.values(mixesRecord).filter(mix => !mix.archivedAt))
      setLoading(false)
      hasInitializedRef.current = true
    }

    loadInitialState()
  }, [user?.id])

  const clearRecipes = async (mixId: string) => {
    const changes = { ...mixesMap }
    let selectedMix = null

    outer:
    for (const [_, mix] of Object.entries(changes)) {
      if (mix.id === mixId) {
        for (const recipe of mix.recipes) {
          recipe.selected = false
        }
        selectedMix = mix
        break outer
      }
    }

    setMixesMap(changes)
    setMixes(Object.values(changes).filter(mix => !mix.archivedAt))

    if (selectedMix) {
      selectedMix = (selectedMix as Mix)

      const data: UpdateMixRequest = {
        name: selectedMix.name,
        recipes: selectedMix.recipes,
      }

      await mixService.updateMix(selectedMix.id, data)
    }
  }

  const createIngredient = async (mixId: string, request: CreateIngredientRequest) => {
    const mix = mixesMap[mixId]
    if (!mix) return

    const now = new Date().toISOString()
    request.id = uuidv4()
    request.createdAt = now
    request.updatedAt = now

    const ingredient: Ingredient = {
      id: request.id,
      name: request.name,
      fullText: request.name,
      quantity: request.quantity,
      unit: request.unit,
      category: request.category,
      sourceType: 'STANDALONE',
      createdAt: request.createdAt,
      updatedAt: request.updatedAt,
    }

    const updatedIngredients = [...mix.ingredients, ingredient]
    const updatedMix = { ...mix, ingredients: updatedIngredients }

    setMixesMap(prev => ({ ...prev, [mixId]: updatedMix }))
    setMixes(prev => {
      const map = { ...Object.fromEntries(prev.map(m => [m.id, m])) }
      map[mixId] = updatedMix
      return Object.values(map).filter(m => !m.archivedAt)
    })

    await ingredientService.createIngredient(mixId, updatedIngredients)
  }

  const createInvitation = async (mixId: string) => {
    const invitationId = uuidv4()

    const mix = mixesMap[mixId]
    if (!mix) return

    const sharerId = user?.id
    if (!sharerId) {
      toast.error('You must be signed in to create an invitation')
      return
    }

    const sharerEmail = user?.email || ''
    const sharerName = (user?.user_metadata?.name as string) || sharerEmail || 'User'
    const sharerImage = (user?.user_metadata?.avatar_url as string) || ''

    // Generate a secure random token (32 bytes = 64 hex chars, same as Go API)
    const tokenBytes = new Uint8Array(32)
    crypto.getRandomValues(tokenBytes)
    const token = Array.from(tokenBytes, byte => byte.toString(16).padStart(2, '0')).join('')

    const expiresAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()

    const innerCreateInvitation = async (): Promise<void> => {
      await invitationService.createInvitation({
        id: invitationId,
        mix_id: mixId,
        mix_name: mix.name,
        sharer_id: sharerId,
        sharer_email: sharerEmail,
        sharer_name: sharerName,
        sharer_image: sharerImage,
        token,
        status: 'PENDING',
        expires_at: expiresAt,
      })
    }

    if (navigator.clipboard) {
      navigator.clipboard.write([
        new ClipboardItem({
          ["text/plain"]: innerCreateInvitation()
            .then(() => {
              const shareUrl = `${window.location.protocol}//${window.location.host}/mixes/${mixId}/invitations/${invitationId}`
              return new Blob([shareUrl], { type: "text/plain" })
            })
            .catch(() => new Blob([''], { type: "text/plain" }))
        })
      ])

      toast.success(`Invitation link copied to the clipboard!`)
    } else {
      toast.success("permission issue: no access to clipboard")
    }
  }

  const createMix = async (request: CreateMixRequest) => {
    try {
      const rawMix = await mixService.createMix(request)
      const mix = convertRealtimeRowToMix(rawMix as unknown as Record<string, unknown>, user?.id)

      if (!mix) {
        toast.error('Failed to create mix: invalid response')
        return
      }

      const changes = { ...mixesMap }
      changes[mix.id] = mix
      setMixesMap(changes)
      setMixes(Object.values(changes).filter(m => !m.archivedAt))

      toast.success(`Mix "${request.name}" created successfully!`)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create mix. Please try again.'
      toast.error(errorMessage)
    }
  }

  const deleteIngredient = async (mixId: string, ingredientId: string, mealSlotId?: string | null, scheduledAt?: string | null) => {
    const mix = mixesMap[mixId]
    if (!mix) return

    const ingredientIndex = mix.ingredients.findIndex(i => {
      let match = i.id === ingredientId
      if (match && mealSlotId != null) {
        match = i.mealSlotId === mealSlotId
      }
      if (match && scheduledAt != null) {
        match = i.scheduledAt === scheduledAt
      }
      return match
    })

    if (ingredientIndex !== -1) {
      const ingredient = mix.ingredients[ingredientIndex]
      const updatedIngredients = [
        ...mix.ingredients.slice(0, ingredientIndex),
        ...mix.ingredients.slice(ingredientIndex + 1),
      ]
      const updatedMix = { ...mix, ingredients: updatedIngredients }

      setMixesMap(prev => ({ ...prev, [mixId]: updatedMix }))
      setMixes(prev => {
        const map = { ...Object.fromEntries(prev.map(m => [m.id, m])) }
        map[mixId] = updatedMix
        return Object.values(map).filter(m => !m.archivedAt)
      })

      try {
        await ingredientService.deleteIngredient(mixId, updatedIngredients)

        toast.success(`Ingredient "${ingredient.name}" removed successfully!`)
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to remove ingredient. Please try again.'
        toast.error(errorMessage)
      }
    }
  }

  const deleteMix = async (mixId: string) => {
    const changes = { ...mixesMap }

    const mix = changes[mixId]

    if (mix) {
      const request: DeleteMixRequest = {
        archivedAt: new Date().toISOString()
      }

      changes[mixId].archivedAt = request.archivedAt

      setMixesMap(changes)
      setMixes(Object.values(changes).filter(mix => !mix.archivedAt))

      try {
        await mixService.deleteMix(mixId, request)

        toast.success(`Mix "${mix.name}" archived successfully!`)
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to remove mix. Please try again.'
        toast.error(errorMessage)
      }
    }
  }

  const deleteRecipe = async (mixId: string, recipeId: string) => {
    const changes = { ...mixesMap }
    const mix = changes[mixId]
    if (!mix) return

    const recipe = mix.recipes.find((r) => r.id === recipeId)
    if (!recipe) return

    const recipeIngredientIds = new Set(recipe.ingredientIds)
    const prevRecipes = mix.recipes
    const prevIngredients = mix.ingredients

    changes[mixId] = {
      ...mix,
      recipes: mix.recipes.filter((r) => r.id !== recipeId),
      ingredients: mix.ingredients.filter((i) => !recipeIngredientIds.has(i.id)),
    }

    setMixesMap(changes)
    setMixes(Object.values(changes).filter((m) => !m.archivedAt))

    try {
      await mixService.updateMix(mixId, {
        name: mix.name,
        recipes: changes[mixId].recipes,
        ingredients: changes[mixId].ingredients,
      })

      toast.success(`Recipe "${recipe.name}" removed from mix`)
    } catch (err) {
      // Roll back optimistic update
      changes[mixId].recipes = prevRecipes
      changes[mixId].ingredients = prevIngredients
      setMixesMap(changes)
      setMixes(Object.values(changes).filter((m) => !m.archivedAt))

      const message = err instanceof Error ? err.message : 'Failed to remove recipe'
      toast.error(message)
    }
  }

  const getIngredients = (mixId: string): Ingredient[] => {
    const mix = mixesMap[mixId]
    if (!mix) {
      return []
    }

    return mix.ingredients
  }

  const getOwnership = (mixId: string): Ownership => {
    return mixesMap[mixId]?.ownership
  }

  const getRecipe = (mixId: string, recipeId: string): Recipe | undefined => {
    return mixesMap[mixId]?.recipes.find(r => r.id === recipeId)
  }

  const getRecipes = (mixId: string): Recipe[] => {
    return Object.values(mixesMap || {}).reduce<Recipe[]>((acc, mix: Mix) => {
      if (mix.id === mixId && mix.recipes) {
        acc.push(...mix.recipes)
      }
      return acc
    }, []).sort((a, b) => {
      if (a.updatedAt < b.updatedAt) {
        return 1
      } else if (a.updatedAt > b.updatedAt) {
        return -1
      } else {
        return 0
      }
    })
  }

  const getSelectedRecipes = (mixId: string): Recipe[] => getRecipes(mixId).filter(recipe => recipe.selected === true)

  const getSelectedRecipesIngredients = (mixId: string): Ingredient[] => {
    const ingredients: Ingredient[] = []
    const mix = mixesMap[mixId]

    if (mix) {
      // STANDALONE
      ingredients.push(...mix.ingredients.filter(i => i.sourceType === 'STANDALONE'))

      // RECIPE
      const recipes = mix.recipes

      if (recipes) {
        const ingedientIds: string[] = []

        for (const recipe of recipes.filter(r => r.selected)) {
          ingedientIds.push(...recipe.ingredientIds)
        }

        ingredients.push(...mix.ingredients.filter(i => ingedientIds.includes(i.id)))
      }
    }

    return ingredients
  }

  const isMix = (mixId: string): boolean => !!mixesMap[mixId]

  // const sendWebSocketMessage = (messageType: WebSocketMessageType, message: WebSocketPayload) => {
  //   if (connectionState === 'connected') {
  //     websocketService.send(messageType, message)
  //   } else {
  //     console.error("websocket connection not connected:", connectionState)
  //   }
  // }

  const showMixToasts = (mix: Mix) => {
    if (!mix.ownership.isOwner && mix.archivedAt) {
      toast.success(`Mix "${mix.name}" archived successfully!`)
    }
  }

  // const showRecipeDiffToast = (recipe: Recipe) => {
  //   const fallbackAvatar = `https://ui-avatars.com/api/?name=${encodeURIComponent(recipe.sharerName || 'User')}&background=10B981&color=fff&size=48`
  //   let message = `Recipe "${recipe.name}" added`
  //
  //   if (user?.imageUrl === recipe.sharerImage) {
  //     toast.success(message)
  //   } else {
  //     message = `Recipe "${recipe.name}" added by ${recipe.sharerName}`
  //
  //     toast(
  //       <div className="bg-card rounded-md shadow-sm p-4 flex items-center gap-4 max-w-md w-full">
  //         <img
  //           src={recipe.sharerImage || fallbackAvatar}
  //           alt={recipe.sharerName}
  //           className="w-12 h-12 rounded-full object-cover flex-shrink-0"
  //           onError={(e) => {
  //             e.currentTarget.src = fallbackAvatar
  //           }}
  //         />
  //         <div className="flex-1 min-w-0">
  //           <p className="text-sm font-medium text-foreground">
  //             {message}
  //           </p>
  //         </div>
  //         <div className="flex-shrink-0">
  //           <CheckCircle className="h-5 w-5 text-green-500" />
  //         </div>
  //       </div>,
  //       {
  //         duration: 3000,
  //         style: {
  //           background: 'transparent',
  //           boxShadow: 'none',
  //           padding: 0,
  //         },
  //       }
  //     )
  //   }
  // }

  const showInvitationAcceptanceToast = (invitation: InvitationDTO) => {
    const fallbackAvatar = `https://ui-avatars.com/api/?name=${encodeURIComponent(invitation.usedByName || 'User')}&background=10B981&color=fff&size=48`

    // Simple logic using isUsedByMe field (now properly computed for WebSocket)
    let message: string
    let avatar: string = ''
    let avatarAlt: string = ''
    let showAvatar = true

    if (invitation.isUsedByMe) {
      // Current user accepted the invitation - no avatar needed
      message = `You accepted an invitation to join "${invitation.mixName}"`
      showAvatar = false
    } else {
      // Another collaborator accepted an invitation
      message = `${invitation.usedByName} accepted an invitation to join "${invitation.mixName}" mix`
      avatar = invitation.usedByImage || fallbackAvatar
      avatarAlt = invitation.usedByName || 'User'
    }

    toast(
      <div className="bg-card rounded-md shadow-sm p-4 flex items-center gap-4 max-w-md w-full">
        {showAvatar && (
          <img
            src={avatar}
            alt={avatarAlt}
            className="w-12 h-12 rounded-full object-cover flex-shrink-0"
            onError={(e) => {
              e.currentTarget.src = fallbackAvatar
            }}
          />
        )}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground">
            {message}
          </p>
        </div>
        <div className="flex-shrink-0">
          <CheckCircle className="h-5 w-5 text-green-500" />
        </div>
      </div>,
      {
        duration: 3000,
        style: {
          background: 'transparent',
          boxShadow: 'none',
          padding: 0,
        },
      }
    )
  }

  // Check if current user should see this acceptance notification
  const shouldShowAcceptanceNotification = (invitation: InvitationDTO): boolean => {
    // @TODO: Handle multi-tab synchronization (USER_INITIALIZE)
    // @TODO: Revisit memory usage for large numbers of invitations
    // @TODO: Add notification history/persistence (future work)
    // @TODO: Revisit keeping invitations forever (cleanup strategy)
    // @TODO: Handle cancelled invitations (future work)

    return Boolean(
      invitation.status === 'ACCEPTED' &&     // Status is accepted
      invitation.usedByName &&                // Acceptor info is available
      invitation.usedByImage                  // Acceptor image is available
    )
  }

  const markIngredient = async (mixId: string, ingredientId: string, markedType: IngredientMarkedType = 'SELFCART', mealSlotId?: string | null, scheduledAt?: string | null) => {
    const mix = mixesMap[mixId]
    if (!mix) return

    let updatedIngredients: Ingredient[] | undefined

    for (let i = 0; i < mix.ingredients.length; i++) {
      const ingredient = mix.ingredients[i]
      let match = ingredient.id === ingredientId
      if (match && mealSlotId != null) {
        match = ingredient.mealSlotId === mealSlotId
      }
      if (match && scheduledAt != null) {
        match = ingredient.scheduledAt === scheduledAt
      }
      if (match) {
        const updatedIngredient = ingredient.markedAt
          ? { ...ingredient, markedAt: undefined, markedType: undefined }
          : { ...ingredient, markedAt: new Date().toISOString(), markedType }

        updatedIngredients = [
          ...mix.ingredients.slice(0, i),
          updatedIngredient,
          ...mix.ingredients.slice(i + 1),
        ]
        break
      }
    }

    if (!updatedIngredients) return

    const updatedMix = { ...mix, ingredients: updatedIngredients }

    setMixesMap(prev => ({ ...prev, [mixId]: updatedMix }))
    setMixes(prev => {
      const map = { ...Object.fromEntries(prev.map(m => [m.id, m])) }
      map[mixId] = updatedMix
      return Object.values(map).filter(m => !m.archivedAt)
    })

    await ingredientService.selectIngredient(mixId, updatedIngredients)
  }

  const selectRecipe = async (mixId: string, recipeId: string, selected: boolean) => {
    const existingMix = mixesMap[mixId]
    if (!existingMix) return

    const recipeIndex = existingMix.recipes.findIndex(r => r.id === recipeId)
    if (recipeIndex === -1) return

    // Build new recipes array (no in-place mutation)
    const newRecipes = existingMix.recipes.map((r, i) => {
      if (i !== recipeIndex) return r
      return {
        ...r,
        selected,
        selectedBy: selected ? user?.id ?? null : null,
        selectedByName: selected ? (user?.user_metadata?.full_name as string) ?? null : null,
      }
    })

    const newIngredients = existingMix.ingredients

    // Optimistic update
    const changes = { ...mixesMap }
    changes[mixId] = { ...existingMix, recipes: newRecipes, ingredients: newIngredients }
    setMixesMap(changes)
    setMixes(Object.values(changes).filter(mix => !mix.archivedAt))

    await mixService.selectRecipe(mixId, newRecipes, newIngredients)
  }

  const favoriteRecipe = async (mixId: string, recipeId: string, action: 'add' | 'remove') => {
    const changes = { ...mixesMap }

    for (const recipe of changes[mixId].recipes) {
      if (recipe.id === recipeId) {
        const favorites = recipe.favorites || []
        if (action === 'add') {
          const already = favorites.some((f) => f.userId === user?.id)
          if (!already) {
            recipe.favorites = [
              ...favorites,
              { userId: user?.id ?? '', userName: (user?.user_metadata?.full_name as string) ?? 'User' },
            ]
          }
        } else {
          recipe.favorites = favorites.filter((f) => f.userId !== user?.id)
        }
      }
    }

    setMixesMap(changes)
    setMixes(Object.values(changes).filter((mix) => !mix.archivedAt))

    await mixService.favoriteRecipe(mixId, recipeId, action)
  }

  const contextValue: WebSocketContextValue = {
    clearRecipes,
    connectionState,
    createIngredient,
    createInvitation,
    createMix,
    deleteIngredient,
    deleteMix,
    deleteRecipe,
    getIngredients,
    getOwnership,
    getRecipe,
    getRecipes,
    getSelectedRecipes,
    getSelectedRecipesIngredients,
    isMix,
    loading,
    markIngredient,
    mixes,
    onEvent,
    selectRecipe,
    favoriteRecipe,
    setLoading,
    invitationsMap,
  }

  return (
    <WebSocketContext.Provider value={contextValue}>
      {children}
    </WebSocketContext.Provider>
  )
}

export function useWebSocketContext(): WebSocketContextValue {
  const context = useContext(WebSocketContext)
  if (!context) {
    throw new Error('useWebSocketContext must be used within a WebSocketProvider')
  }
  return context
}
